import React, { useState, useEffect } from 'react';
import { 
  UserCheck, Plus, Search, Building2, Users, Target, 
  ExternalLink, Mail, Phone, Globe, Shield, X, Check, ArrowRight,
  Award, Sparkles, Clock, Radio, Eye, ChevronRight, Calendar, DollarSign,
  Send, AlertCircle, CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { managerAPI } from '../utils/api';

const ManagerBDs = () => {
  const navigate = useNavigate();
  const [bds, setBds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    country: 'India'
  });

  // BD Request By ID State
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestTargetId, setRequestTargetId] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [sentInvites, setSentInvites] = useState([]);
  const [showInvitesModal, setShowInvitesModal] = useState(false);

  // Drilldown 1: Selected BD -> Agencies & Commissions
  const [selectedBd, setSelectedBd] = useState(null);
  const [bdAgencies, setBdAgencies] = useState([]);
  const [loadingBdAgencies, setLoadingBdAgencies] = useState(false);

  // Drilldown 2: Selected Agency -> Hosts & Commissions
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyHosts, setAgencyHosts] = useState([]);
  const [loadingAgencyHosts, setLoadingAgencyHosts] = useState(false);

  // Drilldown 3: Selected Host -> Host Center Live Sessions
  const [selectedHost, setSelectedHost] = useState(null);
  const [hostSessions, setHostSessions] = useState([]);
  const [loadingHostSessions, setLoadingHostSessions] = useState(false);

  const fetchBDs = async () => {
    try {
      setLoading(true);
      const res = await managerAPI.getBDs();
      if (res.data?.success) {
        setBds(res.data.bds || []);
      }
    } catch (err) {
      console.error("Failed to fetch BDs:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSentInvites = async () => {
    try {
      const res = await managerAPI.getBDInvites();
      if (res.data?.success) {
        setSentInvites(res.data.invitations || []);
      }
    } catch (err) {
      console.error("Failed to fetch BD invites:", err);
    }
  };

  useEffect(() => {
    fetchBDs();
    fetchSentInvites();
  }, []);

  const handleSendBdRequest = async (e) => {
    e.preventDefault();
    if (!requestTargetId.trim()) {
      alert("Please enter the user or host ID number (XOXO ID).");
      return;
    }
    setRequestLoading(true);
    try {
      const res = await managerAPI.inviteBD({ xoxo_id: requestTargetId.trim(), target_user_id: requestTargetId.trim() });
      if (res.data?.success) {
        alert(res.data.message || 'BD request successfully sent to the ID!');
        setShowRequestModal(false);
        setRequestTargetId('');
        fetchSentInvites();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to send BD request to this ID.');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleCreateBD = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      const res = await managerAPI.createBD(formData);
      if (res.data?.success) {
        alert(res.data.message || 'BD successfully onboarded!');
        setShowAddModal(false);
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          password: '',
          country: 'India'
        });
        fetchBDs();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to onboard BD');
    } finally {
      setAddLoading(false);
    }
  };

  // Open BD Drilldown: load agencies
  const handleOpenBdHierarchy = async (bd) => {
    setSelectedBd(bd);
    setLoadingBdAgencies(true);
    try {
      const res = await managerAPI.getBD(bd.id);
      if (res.data?.success) {
        setBdAgencies(res.data.agencies || []);
      }
    } catch (err) {
      console.error("Failed to load BD agencies:", err);
    } finally {
      setLoadingBdAgencies(false);
    }
  };

  // Open Agency Drilldown: load hosts with full commission breakdown
  const handleOpenAgencyHosts = async (agency) => {
    setSelectedAgency(agency);
    setLoadingAgencyHosts(true);
    try {
      const res = await managerAPI.getAgency(agency.id);
      if (res.data?.success) {
        setAgencyHosts(res.data.hosts || []);
      }
    } catch (err) {
      console.error("Failed to load agency hosts:", err);
    } finally {
      setLoadingAgencyHosts(false);
    }
  };

  // Open Host Drilldown: load live streaming sessions
  const handleOpenHostSessions = async (hostId) => {
    setLoadingHostSessions(true);
    try {
      const res = await managerAPI.getHostDetails(hostId);
      if (res.data?.success) {
        setSelectedHost(res.data.host);
        setHostSessions(res.data.sessions || []);
      }
    } catch (err) {
      console.error("Failed to load host sessions:", err);
    } finally {
      setLoadingHostSessions(false);
    }
  };

  const filteredBDs = bds.filter(b => {
    const term = search.toLowerCase();
    return (
      (b.first_name && b.first_name.toLowerCase().includes(term)) ||
      (b.last_name && b.last_name.toLowerCase().includes(term)) ||
      (b.user_id && b.user_id.toLowerCase().includes(term)) ||
      (b.email && b.email.toLowerCase().includes(term)) ||
      (b.phone && b.phone.includes(term))
    );
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-3">
              <UserCheck className="text-indigo-400" size={28} />
              <span>Business Development Hierarchy</span>
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Full multi-level tracking: BD Partner ➔ Managed Agencies ➔ Hosts Target Center ➔ Live Stream Sessions & Commissions.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowRequestModal(true)}
              className="px-5 py-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-lg shadow-emerald-600/30 flex items-center gap-2 cursor-pointer transition-all w-fit"
            >
              <Send size={18} />
              <span>Send BD Request (By ID)</span>
            </button>

            <button
              onClick={() => {
                fetchSentInvites();
                setShowInvitesModal(true);
              }}
              className="px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 font-bold text-sm flex items-center gap-2 cursor-pointer transition-all w-fit"
            >
              <Clock size={16} className="text-amber-400" />
              <span>Sent Requests ({sentInvites.filter(i => i.status === 'pending').length})</span>
            </button>

            <button
              onClick={() => setShowAddModal(true)}
              className="px-5 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-sm shadow-lg shadow-indigo-600/30 flex items-center gap-2 cursor-pointer transition-all w-fit"
            >
              <Plus size={18} />
              <span>Onboard New BD</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search BD by name, pure numeric ID (e.g. 129937), email or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#0b0f19]/80 border border-indigo-500/15 rounded-2xl py-4 pl-14 pr-5 text-white placeholder:text-slate-500 outline-none focus:border-indigo-500/50 text-sm"
          />
        </div>

        {/* BD Cards Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
          </div>
        ) : filteredBDs.length === 0 ? (
          <div className="bg-[#0b0f19]/50 border border-indigo-500/15 rounded-3xl p-12 text-center">
            <UserCheck size={48} className="mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-bold text-white mb-1">No BD Executives Found</h3>
            <p className="text-slate-400 text-sm max-w-md mx-auto">
              {search ? 'No team members matched your search query.' : 'You have not onboarded any BDs yet. Click the button above to onboard your first BD.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBDs.map((bd) => (
              <div 
                key={bd.id}
                className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl hover:border-indigo-500/40 transition-all duration-300 flex flex-col justify-between group"
              >
                <div className="relative z-10">
                  {/* Top: Avatar & Status */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-base shadow-lg shadow-indigo-500/20">
                        {bd.first_name?.charAt(0) || 'B'}
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-base leading-tight">
                          {bd.first_name} {bd.last_name}
                        </h3>
                        <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 text-xs font-mono font-bold border border-indigo-500/20">
                          ID: {bd.user_id}
                        </span>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                      bd.is_active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400'
                    }`}>
                      {bd.is_active ? 'Active' : 'Suspended'}
                    </span>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-1.5 text-xs text-slate-400 mb-6 bg-white/[0.02] p-3 rounded-2xl border border-white/5">
                    {bd.email && (
                      <div className="flex items-center gap-2 truncate">
                        <Mail size={14} className="text-slate-500 shrink-0" />
                        <span className="truncate">{bd.email}</span>
                      </div>
                    )}
                    {bd.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-slate-500 shrink-0" />
                        <span>{bd.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Globe size={14} className="text-slate-500 shrink-0" />
                      <span>{bd.country || 'India'}</span>
                    </div>
                  </div>

                  {/* 3 Metric Pills */}
                  <div className="grid grid-cols-3 gap-2 text-center mb-6">
                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-2.5">
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Agencies</p>
                      <p className="text-base font-black text-white mt-0.5">{bd.agencies_count || 0}</p>
                    </div>
                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-2.5">
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Hosts</p>
                      <p className="text-base font-black text-cyan-400 mt-0.5">{bd.hosts_count || 0}</p>
                    </div>
                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-2.5">
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Coins</p>
                      <p className="text-xs font-bold text-amber-400 mt-1 font-mono truncate">
                        {(bd.monthly_target_coins || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="pt-4 border-t border-white/5 space-y-2">
                  <button
                    onClick={() => handleOpenBdHierarchy(bd)}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-indigo-600/30 to-purple-600/30 hover:from-indigo-600/50 hover:to-purple-600/50 border border-indigo-500/30 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-lg shadow-indigo-600/10"
                  >
                    <Eye size={15} className="text-indigo-400" />
                    <span>Inspect Hierarchy & Commissions</span>
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/manager/agencies?bd_id=${bd.id}`)}
                      className="flex-1 py-2 px-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] text-slate-300 font-medium text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                    >
                      <Building2 size={13} className="text-slate-400" />
                      <span>Agencies</span>
                    </button>
                    <button
                      onClick={() => navigate(`/manager/hosts?bd_id=${bd.id}`)}
                      className="flex-1 py-2 px-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] text-slate-300 font-medium text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                    >
                      <Users size={13} className="text-slate-400" />
                      <span>Hosts</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DRILLDOWN LEVEL 1: BD Agencies Modal */}
        {selectedBd && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0f1026] border border-indigo-500/30 rounded-[32px] p-6 md:p-8 max-w-4xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
              <button 
                onClick={() => setSelectedBd(null)}
                className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <X size={20} />
              </button>

              {/* BD Info Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-600 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-indigo-500/20">
                  {selectedBd.first_name?.charAt(0) || 'B'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-white">{selectedBd.first_name} {selectedBd.last_name}</h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 text-xs font-mono font-bold border border-indigo-500/20">
                      ID: {selectedBd.user_id}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Managed Portfolio • Click on any agency to drill down into Host Center targets & live sessions
                  </p>
                </div>
              </div>

              {/* Agencies List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    <Building2 size={16} className="text-indigo-400" />
                    <span>Agencies Under This BD ({bdAgencies.length})</span>
                  </h4>
                </div>

                {loadingBdAgencies ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-3 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mx-auto"></div>
                    <p className="text-xs text-slate-400 mt-2">Loading agencies & commission breakdown...</p>
                  </div>
                ) : bdAgencies.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 text-xs bg-white/[0.01] rounded-2xl border border-white/5">
                    No agencies assigned to this BD yet.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {bdAgencies.map((agency) => (
                      <div
                        key={agency.id}
                        className="bg-white/[0.02] border border-white/5 hover:border-indigo-500/30 rounded-2xl p-5 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 font-bold text-base">
                            {agency.agency_name?.charAt(0) || 'A'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-white text-base">{agency.agency_name}</p>
                              <span className="text-xs font-mono font-bold text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded-lg border border-indigo-500/20">
                                #{agency.agency_code}
                              </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">
                              Manager: <span className="text-slate-300 font-medium">{agency.real_name || 'N/A'}</span>
                            </p>
                          </div>
                        </div>

                        {/* Metrics: Hosts, Coins, Agency Comm, BD Comm, Manager Comm */}
                        <div className="flex flex-wrap items-center gap-3 md:gap-5 text-center">
                          <div className="bg-white/[0.02] px-3 py-2 rounded-xl border border-white/5">
                            <p className="text-[10px] text-slate-400 uppercase font-bold">Hosts</p>
                            <p className="text-sm font-bold text-cyan-400">{agency.total_hosts || 0}</p>
                          </div>
                          <div className="bg-white/[0.02] px-3 py-2 rounded-xl border border-white/5">
                            <p className="text-[10px] text-slate-400 uppercase font-bold">Coins</p>
                            <p className="text-sm font-bold text-amber-400 font-mono">
                              {(Number(agency.total_coins) || 0).toLocaleString()}
                            </p>
                          </div>
                          <div className="bg-purple-500/10 px-3 py-2 rounded-xl border border-purple-500/20">
                            <p className="text-[10px] text-purple-300 uppercase font-bold">Agency Comm</p>
                            <p className="text-sm font-bold text-purple-400 font-mono">
                              {(Number(agency.total_agency_commission_diamonds) || 0).toLocaleString()} 💎
                            </p>
                          </div>
                          <div className="bg-indigo-500/10 px-3 py-2 rounded-xl border border-indigo-500/20">
                            <p className="text-[10px] text-indigo-300 uppercase font-bold">BD Comm</p>
                            <p className="text-sm font-bold text-indigo-400 font-mono">
                              {(Number(agency.total_bd_commission_diamonds) || 0).toLocaleString()} 💎
                            </p>
                          </div>
                          <div className="bg-emerald-500/10 px-3 py-2 rounded-xl border border-emerald-500/20">
                            <p className="text-[10px] text-emerald-300 uppercase font-bold">Manager Comm</p>
                            <p className="text-sm font-bold text-emerald-400 font-mono">
                              {(Number(agency.total_manager_commission_diamonds) || 0).toLocaleString()} 💎
                            </p>
                          </div>

                          <button
                            onClick={() => handleOpenAgencyHosts(agency)}
                            className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-md shadow-indigo-600/20"
                          >
                            <span>Inspect Hosts</span>
                            <ChevronRight size={15} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DRILLDOWN LEVEL 2: Agency Hosts & Targets Modal */}
        {selectedAgency && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0b0f19] border border-purple-500/30 rounded-[32px] p-6 md:p-8 max-w-5xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
              <button 
                onClick={() => setSelectedAgency(null)}
                className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <X size={20} />
              </button>

              {/* Agency Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-purple-500/20">
                  {selectedAgency.agency_name?.charAt(0) || 'A'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-white">{selectedAgency.agency_name}</h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 text-xs font-mono font-bold border border-purple-500/20">
                      Code: #{selectedAgency.agency_code}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Live Targets & Host Center • Click "View Sessions" on any host to inspect full live sessions and gifts
                  </p>
                </div>
              </div>

              {/* Hosts Table */}
              {loadingAgencyHosts ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-3 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto"></div>
                  <p className="text-xs text-slate-400 mt-2">Loading host target levels & earnings...</p>
                </div>
              ) : agencyHosts.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-xs bg-white/[0.01] rounded-2xl border border-white/5">
                  No hosts currently active in this agency.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-white/5 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-white/[0.02]">
                        <th className="py-3 px-4">Host</th>
                        <th className="py-3 px-3 text-center">Target Level</th>
                        <th className="py-3 px-3 text-right">Coins</th>
                        <th className="py-3 px-3 text-center">Live Days / Time</th>
                        <th className="py-3 px-3 text-right">Host Salary</th>
                        <th className="py-3 px-3 text-right">Agency Comm</th>
                        <th className="py-3 px-3 text-right">BD Comm</th>
                        <th className="py-3 px-3 text-right">Manager Comm</th>
                        <th className="py-3 px-4 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-xs font-medium">
                      {agencyHosts.map((h) => {
                        const levelNum = Number(h.current_level ?? h.target_level_numeric ?? 0);
                        const validDays = Number(h.valid_days || 0);
                        const durationMins = Number(h.duration_mins || 0);
                        const durationHours = Math.floor(durationMins / 60);

                        return (
                          <tr key={h.id || h.user_id} className="hover:bg-white/[0.02] transition-colors">
                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-xs overflow-hidden">
                                  {h.avatar_url ? (
                                    <img src={h.avatar_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    (h.first_name || h.user_name || 'H').charAt(0).toUpperCase()
                                  )}
                                </div>
                                <div>
                                  <p className="font-bold text-white text-xs">
                                    {h.first_name ? `${h.first_name} ${h.last_name || ''}`.trim() : (h.user_name || 'Host')}
                                  </p>
                                  <p className="text-[10px] text-slate-400 font-mono">
                                    ID: {h.xoxo_id || h.user_id || 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </td>

                            <td className="py-3.5 px-3 text-center">
                              <span className="px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-300 font-bold text-[11px] border border-indigo-500/30">
                                Lv.{levelNum}
                              </span>
                            </td>

                            <td className="py-3.5 px-3 text-right">
                              <span className="font-bold text-amber-400 font-mono">
                                {(Number(h.achieved_coins || h.monthly_target_coins) || 0).toLocaleString()}
                              </span>
                            </td>

                            <td className="py-3.5 px-3 text-center">
                              <div className="inline-flex flex-col items-center">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  validDays >= 10 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/15 text-amber-300'
                                }`}>
                                  {validDays} / 10 Days
                                </span>
                                <span className="text-[10px] text-slate-400 mt-0.5">
                                  {durationHours}h {durationMins % 60}m
                                </span>
                              </div>
                            </td>

                            <td className="py-3.5 px-3 text-right">
                              <div className="flex flex-col items-end">
                                <span className={`font-mono font-bold ${validDays >= 10 ? 'text-cyan-400' : 'text-slate-200'}`}>
                                  {(Number(validDays >= 10 ? h.host_salary_diamonds : (h.host_earned_salary_diamonds || h.host_instant_diamonds || Math.floor((h.host_salary_diamonds || 0) * 0.70))) || 0).toLocaleString()} 💎
                                </span>
                                {validDays >= 10 ? (
                                  <span className="text-[9px] text-emerald-400 font-bold bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 mt-0.5 whitespace-nowrap">
                                    Full + Bonus (💎{(Number(h.host_locked_diamonds || Math.floor((h.host_salary_diamonds || 0) * 0.30)) || 0).toLocaleString()})
                                  </span>
                                ) : (
                                  <span className="text-[9px] text-amber-400/90 font-medium bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20 mt-0.5 whitespace-nowrap">
                                    Basic 70% (💎{(Number(h.host_locked_diamonds || Math.floor((h.host_salary_diamonds || 0) * 0.30)) || 0).toLocaleString()} held)
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="py-3.5 px-3 text-right font-mono font-bold text-purple-300">
                              {(Number(h.agency_comm_diamonds) || 0).toLocaleString()} 💎
                            </td>

                            <td className="py-3.5 px-3 text-right font-mono font-bold text-indigo-300">
                              {(Number(h.bd_comm_diamonds) || 0).toLocaleString()} 💎
                            </td>

                            <td className="py-3.5 px-3 text-right font-mono font-bold text-emerald-400">
                              {(Number(h.manager_comm_diamonds) || 0).toLocaleString()} 💎
                            </td>

                            <td className="py-3.5 px-4 text-center">
                              <button
                                onClick={() => handleOpenHostSessions(h.id || h.user_id)}
                                className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-cyan-400 text-[11px] font-bold inline-flex items-center gap-1 cursor-pointer transition-colors"
                              >
                                <Radio size={12} />
                                <span>Sessions</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* DRILLDOWN LEVEL 3: Host Center Live Sessions Modal */}
        {selectedHost && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0b0f19] border border-cyan-500/30 rounded-[32px] p-6 md:p-8 max-w-4xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
              <button 
                onClick={() => setSelectedHost(null)}
                className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <X size={20} />
              </button>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-xl overflow-hidden">
                  {selectedHost.avatar_url ? (
                    <img src={selectedHost.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (selectedHost.first_name || 'H').charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-white">
                      {selectedHost.first_name ? `${selectedHost.first_name} ${selectedHost.last_name || ''}`.trim() : (selectedHost.user_name || 'Host')}
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 text-xs font-mono font-bold border border-cyan-500/20">
                      ID: {selectedHost.xoxo_id || selectedHost.user_id || 'N/A'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Agency: <span className="text-white font-medium">{selectedHost.agency_name}</span> (#{selectedHost.agency_code}) • BD: <span className="text-purple-300 font-medium">{selectedHost.bd_name}</span>
                  </p>
                </div>
              </div>

              {/* Host Center 4 Stat Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Target Level</p>
                  <p className="text-base font-black text-indigo-400 mt-0.5">Lv.{Number(selectedHost.current_level || 0)}</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Valid Days</p>
                  <p className="text-base font-black text-emerald-400 mt-0.5">{selectedHost.valid_days || 0} / 10</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Host Reward</p>
                  <p className="text-base font-black text-cyan-400 mt-0.5 font-mono">
                    {(Number(selectedHost.host_salary_diamonds) || 0).toLocaleString()} 💎
                  </p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-emerald-300 uppercase font-bold">Manager Commission</p>
                  <p className="text-base font-black text-emerald-400 mt-0.5 font-mono">
                    {(Number(selectedHost.manager_commission_diamonds) || 0).toLocaleString()} 💎
                  </p>
                </div>
              </div>

              {/* Sessions Table */}
              <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Radio size={16} className="text-cyan-400" />
                <span>Live Streaming Sessions History ({hostSessions.length})</span>
              </h4>

              {loadingHostSessions ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto"></div>
                </div>
              ) : hostSessions.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-xs bg-white/[0.01] rounded-2xl border border-white/5">
                  No live streaming sessions recorded yet this month.
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {hostSessions.map((s, idx) => (
                    <div key={s.id || idx} className="p-3 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${
                          s.is_valid_day ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-slate-400'
                        }`}>
                          {s.is_valid_day ? '✓' : '•'}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white">
                            {s.room_id ? `Room #${s.room_id}` : 'Live Broadcast'}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {s.created_at ? new Date(s.created_at).toLocaleString() : 'Recent'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-cyan-400">{s.duration_mins || 0} mins</p>
                        <p className="text-[10px] text-amber-400 font-mono mt-0.5">
                          +{(Number(s.coins_earned) || 0).toLocaleString()} coins
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onboard BD Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0f1026] border border-indigo-500/20 rounded-[32px] p-8 max-w-lg w-full shadow-2xl relative">
              <button 
                onClick={() => setShowAddModal(false)}
                className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <X size={20} />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/15 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <UserCheck size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Onboard Business Development (BD)</h3>
                  <p className="text-xs text-slate-400">Pure numeric ID will be generated (no prefixes)</p>
                </div>
              </div>

              <form onSubmit={handleCreateBD} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-400 block mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="e.g. Rahul"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-400 block mb-1">Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="e.g. Sharma"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="bd@company.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1">Phone Number</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="9876543210"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-400 block mb-1">Temporary Password</label>
                    <input
                      type="text"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Default: xoxo@123"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-400 block mb-1">Country</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="India"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                    />
                  </div>
                </div>

                <div className="pt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-5 py-2.5 rounded-xl border border-white/10 text-xs font-bold text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addLoading}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-xs font-bold text-white shadow-lg shadow-indigo-600/30 hover:opacity-90 disabled:opacity-50 cursor-pointer"
                  >
                    {addLoading ? 'Onboarding...' : 'Confirm & Onboard BD'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* SEND BD REQUEST BY ID MODAL */}
        {showRequestModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0f1026] border border-emerald-500/30 rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3 text-emerald-400">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                    <Send size={22} className="text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-white">Send BD Request</h3>
                    <p className="text-xs text-slate-400">Give BD role to any User or Host ID</p>
                  </div>
                </div>
                <button onClick={() => setShowRequestModal(false)} className="text-slate-400 hover:text-white p-2">
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-6 text-xs text-emerald-200 space-y-1">
                <div className="font-bold flex items-center gap-1.5">
                  <CheckCircle2 size={14} /> Official BD Partnership:
                </div>
                <div>• Enter target User ID or Host ID number.</div>
                <div>• The candidate will receive an official BD invite notification & DM.</div>
                <div>• Once accepted, they become your official BD Partner earning 22% commissions!</div>
              </div>

              <form onSubmit={handleSendBdRequest} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block mb-2">
                    Candidate ID Number (XOXO ID / User ID) *
                  </label>
                  <input
                    type="text"
                    required
                    value={requestTargetId}
                    onChange={(e) => setRequestTargetId(e.target.value)}
                    placeholder="Enter ID Number (e.g. 100234 or 129937)"
                    className="w-full bg-[#030712] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-emerald-500 text-sm font-mono font-bold"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Enter the exact XOXO ID number of the user or host you want to promote to BD.
                  </p>
                </div>

                <div className="pt-4 flex items-center justify-end gap-3 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setShowRequestModal(false)}
                    className="px-5 py-2.5 rounded-xl border border-white/10 text-xs font-bold text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={requestLoading}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-xs font-bold text-white shadow-lg shadow-emerald-600/30 hover:opacity-90 disabled:opacity-50 cursor-pointer flex items-center gap-2"
                  >
                    {requestLoading ? 'Sending Request...' : 'Send BD Request Now'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* SENT BD REQUESTS LIST MODAL */}
        {showInvitesModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0f1026] border border-indigo-500/30 rounded-3xl p-8 max-w-2xl w-full shadow-2xl relative max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-4">
                <div className="flex items-center gap-3 text-indigo-400">
                  <Clock size={24} />
                  <div>
                    <h3 className="text-lg font-black text-white">Sent BD Requests</h3>
                    <p className="text-xs text-slate-400">Status of sent Business Development partner requests</p>
                  </div>
                </div>
                <button onClick={() => setShowInvitesModal(false)} className="text-slate-400 hover:text-white p-2">
                  <X size={20} />
                </button>
              </div>

              <div className="overflow-y-auto space-y-3 flex-1 pr-1">
                {sentInvites.length === 0 ? (
                  <div className="p-12 text-center text-slate-500 text-sm">
                    No BD requests sent yet. Click "Send BD Request (By ID)" to send your first BD request.
                  </div>
                ) : (
                  sentInvites.map((inv) => (
                    <div 
                      key={inv.id}
                      className="p-4 rounded-2xl bg-white/[0.03] border border-white/5 flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {inv.first_name?.[0] || 'U'}
                        </div>
                        <div>
                          <div className="font-bold text-white text-sm">
                            {inv.first_name} {inv.last_name || ''}
                          </div>
                          <div className="text-xs font-mono text-indigo-400">
                            XOXO ID: {inv.xoxo_id || inv.user_id}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            Sent on: {new Date(inv.created_at).toLocaleDateString()} ({inv.invite_code})
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
                          inv.status === 'accepted' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                          inv.status === 'rejected' ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' :
                          'bg-amber-500/10 text-amber-400 border border-amber-500/20 animate-pulse'
                        }`}>
                          {inv.status || 'pending'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="pt-4 border-t border-white/10 flex justify-end">
                <button
                  onClick={() => setShowInvitesModal(false)}
                  className="px-5 py-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-slate-300 text-xs font-bold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default ManagerBDs;
